import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PremiumGate from "@/components/PremiumGate";
import InitialAvatar from "@/components/InitialAvatar";
import Breadcrumb from "@/components/Breadcrumb";
import Seo, { breadcrumbSchema, sitterProfileSchema, serviceOfferSchema } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, ShieldCheck, MapPin, Languages, Clock, Award, ArrowLeft, MessageCircle, Heart, HeartOff } from "lucide-react";
import { toast } from "sonner";

export default function SitterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start: "", end: "", children_count: 1, notes: "" });
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [favorited, setFavorited] = useState(false);

  const isFreeParent = user && user.role === "parent" && !user.is_premium;
  const isPremiumParent = user && user.role === "parent" && user.is_premium;

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sitter }, { data: rvs }] = await Promise.all([
          api.get(`/babysitters/${id}`),
          api.get(`/reviews/babysitter/${id}`),
        ]);
        setS(sitter); setReviews(rvs);
        if (isPremiumParent) {
          try {
            const { data: favs } = await api.get("/favorites");
            setFavorited(!!favs.find((f) => f.id === id));
          } catch (_) {}
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, [id, isPremiumParent]);

  const requestBooking = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (isFreeParent) { nav("/pricing"); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        babysitter_id: id,
        start_datetime: new Date(form.start).toISOString(),
        end_datetime: new Date(form.end).toISOString(),
        children_count: Number(form.children_count) || 1,
        notes: form.notes,
      });
      toast.success("Booking requested! You'll hear from the caregiver soon.");
      setForm({ start: "", end: "", children_count: 1, notes: "" });
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    }
    setSubmitting(false);
  };

  const sendMessage = async () => {
    if (!user) { nav("/login"); return; }
    if (isFreeParent) { nav("/pricing"); return; }
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { to_user_id: s.user_id, text: msg });
      toast.success("Message sent");
      setMsg("");
      nav("/dashboard");
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail));
    }
  };

  const toggleFav = async () => {
    if (!user) { nav("/login"); return; }
    if (isFreeParent) { nav("/pricing"); return; }
    try {
      if (favorited) { await api.delete(`/favorites/${id}`); setFavorited(false); toast.info("Removed from favourites"); }
      else { await api.post("/favorites", { babysitter_id: id }); setFavorited(true); toast.success("Saved to favourites"); }
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail));
    }
  };

  if (loading) return <div className="min-h-screen bg-linen"><Nav /><div className="max-w-7xl mx-auto px-5 py-20 text-forest/60">Loading…</div></div>;
  if (!s) return <div className="min-h-screen bg-linen"><Seo title="Caregiver not found" noindex /><Nav /><div className="max-w-7xl mx-auto px-5 py-20 text-forest">Caregiver not found.</div></div>;

  const langLine = (s.languages || []).slice(0, 3).join(", ") || "multiple languages";
  const description = `${s.name} — verified caregiver in ${s.city || "Canada"}${s.postal_code ? " (" + s.postal_code + ")" : ""}. ${s.experience_years || 0} yrs experience, ${langLine}. ${s.headline || s.bio?.slice(0, 120) || "Warm, dedicated caregiver on Sakeenah Circle."}`;
  const path = `/babysitters/${s.slug || s.id}`;
  const bc = [
    { name: "Find a caregiver", path: "/browse" },
    ...(s.city_slug ? [{ name: s.city, path: `/sitters/${s.city_slug}` }] : []),
    { name: s.name },
  ];

  return (
    <div className="min-h-screen bg-linen">
      <Seo
        title={`${s.name} — verified caregiver${s.city ? " in " + s.city : ""}`}
        description={description}
        path={path}
        type="profile"
        jsonLd={[
          breadcrumbSchema(bc),
          sitterProfileSchema(s, reviews),
          serviceOfferSchema(s),
        ]}
      />
      <Nav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 space-y-4">
        <Breadcrumb items={bc} />
        <Link to="/browse" className="inline-flex items-center gap-1 text-sm text-forest/70 hover:text-forest"><ArrowLeft className="w-4 h-4" /> Back to browse</Link>
      </div>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-8">
          <div className="grid md:grid-cols-5 gap-6">
            <div className="md:col-span-2">
              <div className="aspect-square rounded-3xl border border-taupe bg-sage/15 flex items-center justify-center">
                <InitialAvatar name={s.name} size={160} className="!border-0" />
              </div>
            </div>
            <div className="md:col-span-3 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                {s.verified && <span className="badge-gold"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                <span className="badge-sage"><Award className="w-3 h-3" /> {s.experience_years || 0} yrs</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-forest leading-tight" data-testid="sitter-name">{s.name}</h1>
              <p className="text-lg text-forest/80 font-serif italic">{s.headline}</p>
              <div className="flex items-center gap-4 text-sm text-forest/70 flex-wrap">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-gold text-gold" /> {s.rating_avg?.toFixed(1) || "New"} ({s.rating_count || 0})</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {s.city} · {s.postal_code}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-4xl text-forest">${s.hourly_rate}</span>
                <span className="text-xs uppercase tracking-widest text-forest/50">per hour</span>
              </div>
              {user?.role === "parent" && (
                <button onClick={toggleFav} className="btn-outline text-sm flex items-center gap-1.5" data-testid="favorite-btn">
                  {favorited ? <><HeartOff className="w-4 h-4" /> Saved</> : <><Heart className="w-4 h-4" /> Save to favourites</>}
                </button>
              )}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-forest mb-3">About {s.name.split(" ")[0]}</h2>
            <p className="text-forest/80 leading-relaxed">{s.bio || "This caregiver hasn't added a bio yet."}</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="card-organic p-5">
              <ShieldCheck className="w-6 h-6 text-gold" />
              <h4 className="font-serif text-lg mt-2 text-forest">Verifications</h4>
              <ul className="text-sm text-forest/75 mt-2 space-y-1">
                <li>{s.cpr_certified ? "✓" : "—"} CPR certified</li>
                <li>{s.police_check_certified ? "✓" : "—"} Police check</li>
                <li>{s.verified ? "✓" : "—"} Sakeenah verified</li>
              </ul>
            </div>
            <div className="card-organic p-5">
              <Languages className="w-6 h-6 text-gold" />
              <h4 className="font-serif text-lg mt-2 text-forest">Languages</h4>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(s.languages || []).map((l) => <span key={l} className="badge-sage">{l}</span>)}
                {!(s.languages || []).length && <span className="text-xs text-forest/50">—</span>}
              </div>
            </div>
            <div className="card-organic p-5">
              <Clock className="w-6 h-6 text-gold" />
              <h4 className="font-serif text-lg mt-2 text-forest">Availability</h4>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(s.availability || []).map((a) => <span key={a} className="badge-sage">{a}</span>)}
                {!(s.availability || []).length && <span className="text-xs text-forest/50">—</span>}
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-forest mb-4">Family reviews</h2>
            {reviews.length === 0 ? (
              <p className="text-forest/60 italic">No reviews yet — be the first to share your experience.</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className="card-organic p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-lg text-forest">{r.parent_name}</span>
                      <span className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-gold text-gold" : "text-taupe"}`} />
                        ))}
                      </span>
                    </div>
                    <p className="text-forest/80 mt-2 text-sm leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          {isFreeParent ? (
            <PremiumGate
              title="Upgrade to book & message"
              body="Free accounts can browse — Premium unlocks direct messaging with caregivers, booking requests, favourites and AI matching."
              cta="Upgrade — $9.99/mo"
            />
          ) : (
            <div className="card-organic p-6 sticky top-24">
              <h3 className="font-serif text-2xl text-forest">Request a booking</h3>
              <p className="text-sm text-forest/70 mt-1">Send {s.name.split(" ")[0]} a request. They'll respond within a couple of hours.</p>
              <form onSubmit={requestBooking} className="mt-5 space-y-4">
                <div>
                  <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Start</label>
                  <input type="datetime-local" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="input-organic mt-1" data-testid="book-start" />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">End</label>
                  <input type="datetime-local" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="input-organic mt-1" data-testid="book-end" />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Children</label>
                  <input type="number" min="1" max="10" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} className="input-organic mt-1" data-testid="book-children" />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Notes (optional)</label>
                  <textarea rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-organic mt-1" placeholder="Any preferences, allergies, or context…" />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60" data-testid="book-submit">
                  {submitting ? "Sending…" : user ? "Send request" : "Sign in to request"}
                </button>
              </form>

              {isPremiumParent && (
                <div className="mt-6 border-t border-taupe pt-5">
                  <h4 className="font-serif text-lg text-forest flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Send a message</h4>
                  <textarea rows="3" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={`Say hello to ${s.name.split(" ")[0]}…`} className="input-organic mt-2" data-testid="dm-input" />
                  <button onClick={sendMessage} className="btn-outline w-full mt-2 text-sm" data-testid="dm-send">Send message</button>
                </div>
              )}
            </div>
          )}
        </aside>
      </section>
      <Footer />
    </div>
  );
}
